import React, {useCallback, useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {Button, Select, Table, Tag, message} from 'antd'
import {lookupBySku} from '../../../../services/api/apiProductionPlanning'
import {RATE_UNIT_LABELS, stageConfigPath} from '../../constants'
import {FILE_DATA_PAYLOAD_SAMPLE} from '../../constants/glossary'
import {DASH, formatRate} from '../../helpers/format'
import {matchedGroupLabel} from '../../helpers/priority'
import {gotoSkuDetail} from '../../helpers/skuDetail'
import useSkuOptions from '../../helpers/useSkuOptions'
import queryString from 'query-string'
import {setUrlParams} from '../../../../share/urlParams'
import Notice from '../shared/Notice'
import LoadError from '../shared/LoadError'

const {Option} = Select

/**
 * SCR-M16-004 — Tra cứu định mức theo SKU.
 *
 * Chức năng này bù cho việc đã bỏ danh mục nhóm SKU. Không có nó, sau vài tháng sẽ không ai trả lời
 * được câu hỏi cơ bản nhất: "SKU này đang dùng định mức nào ở công đoạn Cắt?"
 *
 * ⚠️ BR-M16-46: kết quả màn này và giờ tính thật ở M17/M18 phải **giống hệt** vì cùng gọi một
 *    `lookupStandard`. Không cài logic tra cứu riêng cho màn hình.
 */

/**
 * Màn này CHỈ chạy trong modal của Chỉ số tiêu chuẩn (`StageListPage`) — không còn mục menu, không
 * còn route riêng. Trước đây ở đây có thêm nhánh "chạy như một trang": nhánh đó đã thành code chết
 * sau khi bỏ route, nên gỡ hẳn thay vì để người sau tưởng còn đường vào. Muốn dựng lại route thì
 * khai ở `routes/view.js` TRƯỚC `:stageCode` và thêm lại nhánh đó.
 *
 * SKU đang chọn nằm trên URL của màn cha — copy đường dẫn gửi cho người khác là họ mở ra đúng SKU
 * mình đang tra.
 *
 * ⚠️ Đọc/ghi `?sku=` THẲNG qua `location`/`history`, KHÔNG dùng `useUrlFilters`. Hook đó đồng bộ
 *    một chiều state → URL (`values` là `useState` chốt lúc mount), nên bấm Back đổi URL mà state
 *    giữ nguyên rồi effect ghi giá trị cũ đè lại: Back từ `?sku=B` về `?sku=A` bị kéo ngược lại `B`
 *    ngay lập tức. Màn cha cũng đã bỏ hook vì đúng lý do này — xem `StageListPage.js`.
 *
 * Nút "Xem"/"Cấu hình" và "Gán quy trình →" vẫn điều hướng thẳng sang màn khác: đó là chủ đích, và
 * vì màn cha mở modal bằng `history.push` nên Back từ màn đó quay lại ĐÚNG modal với đúng SKU.
 */
function LookupPage({history, location, onClose}) {
    /*
     * ⚠️ Phải chuẩn hoá mảng. `?sku=a&sku=b` làm `query-string` trả về một MẢNG MỚI ở mỗi lần parse,
     *    mà `skuId` nằm trong deps của effect tra cứu bên dưới — `Object.is` luôn false ⇒ effect chạy
     *    lại mỗi render ⇒ vòng lặp vô hạn, treo tab và bắn request không giới hạn. `parseUrlFilters`
     *    của `useUrlFilters` vốn có lớp này (`Array.isArray(raw) ? raw[0] : raw`); bỏ hook là phải
     *    mang nó theo.
     */
    const rawSku = queryString.parse(location.search).sku
    const skuId = (Array.isArray(rawSku) ? rawSku[0] : rawSku) || ''

    // `replace` chứ không `push`: mỗi lần đổi SKU trong ô chọn mà đẻ một entry thì Back phải bấm
    // hàng chục lần mới thoát khỏi modal. Entry để Back đóng modal do màn cha đẩy một lần duy nhất.
    const setSku = useCallback((value) => {
        history.replace({
            pathname: location.pathname,
            search: setUrlParams(location.search, {sku: value}),
        })
    }, [history, location.pathname, location.search])
    const [result, setResult] = useState(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)
    // Công đoạn ngoài quy trình: mặc định GẤP. Xem lý do ở `buildRows`.
    const [showNotInRouting, setShowNotInRouting] = useState(false)

    // Đọc qua ref trong effect chạy một lần: cần biết URL có sẵn SKU chưa mà không kéo `skuId` vào
    // deps, vì kéo vào là danh sách SKU bị tải lại mỗi lần đổi lựa chọn.
    const skuIdRef = useRef(skuId)
    const setSkuRef = useRef(setSku)

    skuIdRef.current = skuId
    setSkuRef.current = setSku

    /*
     * Danh mục SKU: tìm phía SERVER (xem `useSkuOptions`). Nạp một lần rồi lọc tại chỗ sẽ im lặng
     * giấu mọi SKU nằm ngoài trang đầu mà service trả.
     *
     * ⚠️ Chốt unmount nay nằm TRONG hook (ref `alive`, che cả lượt nạp đầu lẫn lượt tìm) — và nó
     *    bắt buộc, không phải cho gọn: `onFirstLoad` dưới đây gọi `setSku`, thứ ghi URL bằng
     *    `history.replace`. `history` là singleton của router nên lệnh đó VẪN CHẠY sau unmount;
     *    người dùng bấm Đóng/Back trong lúc danh mục còn đang tải thì lượt trả về muộn sẽ ghi lại
     *    `?lookup=1&sku=…`, modal tự bật lên lại, và vì là `replace` nên nó đè luôn entry lịch sử
     *    của màn danh sách.
     */
    const {skus, skuSearching, skuSearchFailed, searchSkus} = useSkuOptions({
        // Tra được cả SKU đang TẮT: định mức của hàng vừa ngừng sản xuất vẫn phải tra lại được.
        // Độ phủ thì KHÔNG đổi — service vẫn tính trên SKU đang Bật (BR-M16-26).
        includeInactive: true,
        // Mặc định: SKU đầu tiên theo thứ tự bảng chữ cái (§3.2) — nhưng KHÔNG đè lên SKU đã có sẵn
        // trên URL, nếu không link chia sẻ mở ra lại nhảy về SKU đầu danh sách.
        onFirstLoad: (sorted) => {
            if (!skuIdRef.current && sorted.length) setSkuRef.current(sorted[0].skuId)
        },
        onError: setError,
    })

    const fetchLookup = useCallback(async (targetSkuId) => {
        if (!targetSkuId) return

        setLoading(true)
        setError(null)

        try {
            const data = await lookupBySku(targetSkuId)

            setResult(data)

            // E-402 — cấu hình mơ hồ lọt tới lúc chạy là LỖI DỮ LIỆU, lẽ ra đã bị chặn khi lưu.
            const ambiguous = (data.stages || []).find((stage) => (stage.lines || [])
                .some((line) => line.result.status === 'ambiguous'))

            if (ambiguous) {
                message.error(
                    `Phát hiện cấu hình mơ hồ ở công đoạn ${ambiguous.name}. Đây là lỗi dữ liệu, vui lòng báo Dev`
                )
                // eslint-disable-next-line no-console
                console.error('[M16][E-402] Cấu hình mơ hồ khi tra cứu', {skuId: targetSkuId, stage: ambiguous})
            }
        } catch (e) {
            setError(e)
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        fetchLookup(skuId)
    }, [skuId, fetchLookup])

    /**
     * Dàn phẳng thành một dòng cho mỗi dòng NVL. Dòng đầu của mỗi công đoạn giữ đủ thông tin công
     * đoạn; dòng thứ hai trở đi để trống các cột thuộc về công đoạn (§3.3).
     */
    const buildRows = () => {
        const rows = []

        const required = (result && result.stages) || []
        /*
         * Công đoạn NGOÀI tập bắt buộc, nối xuống cuối bảng.
         *
         * ⚠️ Đánh số tiếp tục nhưng đánh dấu `extra` để mọi cột làm nhạt đi: chúng KHÔNG nằm trong
         *    mẫu số "Thiếu x/y". Trộn lẫn mà không phân biệt là người dùng đếm tay ra một con số
         *    khác con số service trả, rồi tin con số nào cũng sai.
         */
        const other = (result && result.otherStages) || []
        /*
         * `on_demand` KHÁC `not_in_routing`, nên hiện khác nhau.
         *
         * · Chụp ảnh sản phẩm CÓ áp dụng cho SKU này — chỉ số của nó thật sự được dùng khi có yêu
         *   cầu chụp (M17/M18). Nó nằm ngoài mẫu số độ phủ chỉ vì không phải item nào cũng chụp
         *   (BR-M16-30). Ẩn đi là giấu việc có thật.
         * · Công đoạn ngoài quy trình thì câu trả lời luôn là "không áp dụng" — hữu ích khi có người
         *   hỏi "sao SKU này không có giờ Cắt", nhưng để mở sẵn thì nó gần như gấp đôi bảng bằng
         *   những dòng không bao giờ phải hành động. Gấp lại, mở khi cần.
         */
        const onDemand = other.filter((stage) => stage.source === 'on_demand')
        const notInRouting = other.filter((stage) => stage.source !== 'on_demand')
        const extra = [...onDemand, ...(showNotInRouting ? notInRouting : [])]

        ;[...required, ...extra].forEach((stage, stageIndex) => {
            (stage.lines || []).forEach((line, lineIndex) => {
                rows.push({
                    key: `${stage.stageCode}-${line.materialCode || 'item'}-${lineIndex}`,
                    isFirst: lineIndex === 0,
                    stageIndex: stageIndex + 1,
                    extra: stageIndex >= required.length,
                    stage,
                    line,
                })
            })
        })

        return rows
    }

    const columns = [
        {
            title: '#',
            key: 'index',
            width: 34,
            render: (text, row) => (row.isFirst ? row.stageIndex : ''),
        },
        {
            title: 'Công đoạn',
            key: 'stage',
            width: 150,
            render: (text, row) => (row.isFirst ? <strong>{row.stage.name}</strong> : ''),
        },
        {
            title: 'Nguồn',
            key: 'source',
            width: 110,
            render: (text, row) => {
                if (!row.isFirst) return ''

                if (row.stage.source === 'on_demand') return <Tag>Theo yêu cầu</Tag>
                if (row.stage.source === 'not_in_routing') return <Tag>Ngoài quy trình</Tag>

                return row.stage.source === 'mandatory'
                    ? <Tag color="orange">Bắt buộc</Tag>
                    : <Tag>Quy trình</Tag>
            },
        },
        {
            title: 'Dòng NVL',
            key: 'material',
            width: 150,
            render: (text, row) => (row.line.materialCode
                ? <Tag color="purple">{row.line.materialName || row.line.materialCode}</Tag>
                : <span className="VcMuted">cả item/đơn</span>),
        },
        {
            title: 'Nhóm áp dụng',
            key: 'group',
            render: (text, row) => {
                const {result: lookup} = row.line

                if (lookup.status === 'matched') {
                    return (
                        <span>
                            {matchedGroupLabel(lookup.priority, lookup)}
                            <span className="VcMuted"> · độ ưu tiên {lookup.priority || 0}</span>
                        </span>
                    )
                }

                if (lookup.status === 'ambiguous') {
                    return <Tag color="red">Cấu hình mơ hồ — báo Dev</Tag>
                }

                /*
                 * "Chưa có dòng NVL" KHÁC "chưa khai nhóm", nên KHÔNG dùng chung nhãn đỏ.
                 *
                 * Việc phải làm cũng khác hẳn: đây là thiếu dữ liệu NVL-theo-SKU (T0.1, chưa có
                 * nguồn), không phải thiếu cấu hình định mức. Gộp vào "Chưa được nhóm nào phủ" là
                 * đẩy IE đi khai thêm nhóm — khai xong vẫn không khớp, vì không có dòng NVL nào để
                 * mà tra.
                 */
                if (lookup.status === 'no_material_data') {
                    return <Tag color="orange">Chưa có dòng NVL cho SKU này</Tag>
                }

                return <Tag color="red">Chưa được nhóm nào phủ</Tag>
            },
        },
        {
            title: 'Đơn vị đo',
            key: 'unit',
            width: 90,
            render: (text, row) => <code>{RATE_UNIT_LABELS[row.stage.rateUnit]}</code>,
        },
        {
            title: 'Chỉ số',
            key: 'rate',
            width: 110,
            render: (text, row) => {
                const {result: lookup} = row.line

                if (lookup.status !== 'matched') return <span className="VcMuted">{DASH}</span>

                return (
                    <code className="VcRate">
                        {formatRate(lookup.rate)} s/{RATE_UNIT_LABELS[row.stage.rateUnit]}
                    </code>
                )
            },
        },
        {
            title: '',
            key: 'action',
            width: 100,
            render: (text, row) => {
                if (!row.isFirst) return ''

                const lines = row.stage.lines || []
                const covered = lines.some((line) => line.result.status === 'matched')
                // Thiếu DỮ LIỆU NVL thì mở màn cấu hình cũng không sửa được gì — đừng mời "Cấu hình".
                const noMaterialData = lines.every((line) => line.result.status === 'no_material_data')

                return (
                    <Button
                        size="small"
                        type={covered || noMaterialData ? 'default' : 'primary'}
                        onClick={() => history.push(stageConfigPath(row.stage.stageCode))}
                    >
                        Xem
                    </Button>
                )
            },
        },
    ]

    const renderSummary = () => {
        if (!result) return null

        /*
         * SKU đang TẮT: nói trước mọi kết luận khác.
         *
         * Nó đổi ý nghĩa của cả màn — "thiếu định mức" ở một SKU đã ngừng sản xuất không phải việc
         * phải đi sửa, và SKU này cũng KHÔNG nằm trong mẫu số độ phủ ở báo cáo (BR-M16-26). Không nói
         * ra thì người dùng đi khai định mức cho hàng không còn chạy nữa.
         */
        const offNotice = result.active === false ? (
            <Notice level="warning">
                SKU này đang <strong>Tắt</strong> — tra cứu được, nhưng nó không nằm trong báo cáo độ
                phủ và không sinh việc ở kế hoạch sản xuất.
            </Notice>
        ) : null

        if (result.status === 'missing_routing') {
            return (
                <>
                {offNotice}
                <Notice
                    level="warning"
                    action="Gán quy trình →"
                    onAction={() => gotoSkuDetail(history, result.skuId)}
                >
                    SKU chưa gán quy trình sản xuất. Không xác định được cần định mức những công đoạn
                    nào. Xử lý ở màn Quản lý SKU.
                </Notice>
                </>
            )
        }

        if (result.missingStageCount > 0) {
            /*
             * GỌI TÊN công đoạn thiếu, và tách theo NGUYÊN NHÂN.
             *
             * ⚠️ Câu cũ ("rơi vào no_standard ở các dòng đỏ") sai hai lần: người đọc phải tự dò xem
             *    dòng nào thiếu, và từ khi `no_material_data` mang nhãn CAM thì có trường hợp không
             *    còn dòng đỏ nào — chỉ đường tới một thứ không tồn tại.
             *
             * Hai nguyên nhân phải tách vì việc phải làm khác hẳn nhau: thiếu nhóm thì IE đi khai,
             * còn thiếu dòng NVL là thiếu DỮ LIỆU nguồn (T0.1) — khai bao nhiêu nhóm cũng không khớp.
             */
            const missingStages = result.stages.filter(
                (stage) => !(stage.lines || []).some((line) => line.result.status === 'matched')
            )
            const noMaterial = missingStages.filter((stage) =>
                (stage.lines || []).every((line) => line.result.status === 'no_material_data'))
            const noGroup = missingStages.filter((stage) => !noMaterial.includes(stage))
            const names = (list) => list.map((stage) => stage.name).join(', ')

            return (
                <>
                {offNotice}
                <Notice level="error">
                    Thiếu <strong>{result.missingStageCount}/{result.totalStageCount}</strong> công đoạn:{' '}
                    <strong>{names(missingStages)}</strong>.
                    <div className="VcNoticeSub">
                        {noGroup.length > 0 && (
                            <div>
                                <strong>{names(noGroup)}</strong> — chưa có nhóm định mức nào phủ. Vào màn
                                Cấu hình chỉ số của công đoạn để khai.
                            </div>
                        )}
                        {noMaterial.length > 0 && (
                            <div>
                                <strong>{names(noMaterial)}</strong> — chưa có dòng NVL cho SKU này, nên
                                không tra được dù đã khai nhóm. Đây là thiếu DỮ LIỆU nguồn, không phải
                                thiếu cấu hình.
                            </div>
                        )}
                        <div>
                            Các dòng <strong>Theo yêu cầu</strong> / <strong>Ngoài quy trình</strong> ở cuối
                            bảng KHÔNG tính vào con số này.
                        </div>
                    </div>
                </Notice>
                </>
            )
        }

        // `offNotice` phải đi kèm CẢ nhánh này. Thiếu nó thì một SKU đã ngừng sản xuất hiện banner
        // xanh "đủ định mức" mà không một dòng nào nói nó nằm ngoài mẫu số độ phủ (BR-M16-26).
        return (
            <>
                {offNotice}
                <Notice level="success">
                    Đủ định mức toàn bộ công đoạn và toàn bộ dòng NVL.
                </Notice>
            </>
        )
    }

    const notInRoutingCount = ((result && result.otherStages) || []).filter(
        (stage) => stage.source !== 'on_demand'
    ).length

    const skuLabel = (sku) => {
        const routing = sku.routingName || (sku.routingCode ? sku.routingCode : 'chưa gán quy trình')

        // Nói rõ SKU đang tắt ngay trên nhãn: tra nhầm một SKU đã ngừng rồi đi sửa định mức cho nó
        // là việc vô ích, và không có dấu hiệu nào khác để nhận ra.
        const off = sku.active === false ? ' — ĐANG TẮT' : ''

        return `${sku.skuId} — ${routing} — ${(sku.materialCodes || []).join(' + ')}${off}`
    }

    return (
        <div className="VcEmbeddedPage VcLookupPage">
            <div className="VcPageHead">
                <h1 className="VcPageTitle">Tra cứu định mức theo SKU</h1>
                <div className="VcPageActions">
                    <Button onClick={onClose}>Đóng</Button>
                </div>
            </div>

            {/*
              * Khối yêu cầu dữ liệu detect đặt ở đây vì đây là nơi Dev và IE hay mở nhất khi cần đối
              * chiếu số liệu (§3.1).
              */}
            <Notice level="info">
                <div>
                    Yêu cầu dữ liệu detect từ tool gen file. Để tra được định mức, mỗi item phải lưu
                    danh sách dòng NVL, mỗi dòng gồm <code>material_code</code>, <code>area</code> (cm²),
                    {' '}<code>cutting_length</code> (cm). Không gộp thành một con số cho cả item.
                </div>
                <pre className="VcCodeBlock">{FILE_DATA_PAYLOAD_SAMPLE}</pre>
                <div>
                    Dòng có <code>cutting_length = 0</code> vẫn phải gửi, vì nó có diện tích và sẽ tính
                    giờ ở công đoạn In.
                </div>
            </Notice>

            <div className="VcFilterBar">
                <div className="VcFilterItem">
                    {/*
                      * Nhãn TỰ KIỂM CHỨNG từ dữ liệu, không khẳng định hành vi backend.
                      *
                      * Nhãn cũ `— chỉ SKU đang Bật` bị gỡ khi màn này bắt đầu xin cả SKU tắt, nhưng
                      * việc service có trả hay không thì chưa ai xác nhận. Khẳng định "gồm cả SKU
                      * Tắt" mà service vẫn lọc là nói dối; im lặng cũng là nói dối theo kiểu cũ.
                      * Nên: thấy ít nhất một SKU tắt thì nói có, chưa thấy thì nói thẳng là chưa
                      * thấy — người dùng biết ngay mình đang ở tình huống nào.
                      *
                      * Hai chỗ phải giữ đúng phạm vi, vì chính nhãn này tự xưng là tự-kiểm-chứng:
                      *
                      * - `skus` rỗng thì KHÔNG nói gì. Lúc đang tải, "chưa thấy SKU nào đang Tắt"
                      *   là khẳng định về dữ liệu chưa có, và nó đổi chữ ngay trước mắt khi data về.
                      * - Nói "trong phần đã tải", không nói "trong danh mục". `useSkuOptions` chỉ
                      *   nhận trang đầu cộng kết quả các lượt tìm, nên suy từ đó ra toàn danh mục
                      *   đúng là lớp nói-quá mà nhãn này sinh ra để tránh.
                      */}
                    <label>
                        Mã SKU{' '}
                        <span className="VcMuted">
                            {skus.length === 0
                                ? ''
                                : skus.some((sku) => sku.active === false)
                                    ? '— gồm cả SKU đang Tắt'
                                    : '— chưa thấy SKU nào đang Tắt trong phần đã tải'}
                        </span>
                    </label>
                    <Select
                        showSearch
                        style={{minWidth: 400}}
                        value={skuId || undefined}
                        optionFilterProp="children"
                        onSearch={searchSkus}
                        notFoundContent={skuSearchFailed
                            ? 'Tìm hỏng — thử lại'
                            : (skuSearching ? 'Đang tìm…' : 'Không có SKU khớp')}
                        onChange={(value) => setSku(value)}
                    >
                        {skus.map((sku) => (
                            <Option key={sku.skuId} value={sku.skuId}>{skuLabel(sku)}</Option>
                        ))}
                    </Select>
                    {/*
                      * Trạng thái THẬT của SKU đang xem, đọc từ chính kết quả tra cứu.
                      *
                      * Không suy từ `skus` phía trình duyệt: danh sách đó gộp dần theo các lượt tìm
                      * nên có thể chưa chứa SKU vừa mở từ URL. `result.active` là thứ service vừa
                      * trả cho đúng SKU này.
                      */}
                    {result && (
                        <span className="VcSkuStatus">
                            {result.active === false
                                ? <Tag color="orange">Đang tắt</Tag>
                                : <Tag color="green">Đang bật</Tag>}
                        </span>
                    )}
                </div>
            </div>

            {error && (
                <LoadError error={error} message="Không tra cứu được định mức. Vui lòng thử lại" onRetry={() => fetchLookup(skuId)}/>
            )}

            {result && result.status !== 'missing_routing' && notInRoutingCount > 0 && (
                <div className="VcHint VcNotInRoutingToggle">
                    <Button size="small" type="link" onClick={() => setShowNotInRouting(!showNotInRouting)}>
                        {showNotInRouting ? 'Ẩn' : 'Hiện'} {notInRoutingCount} công đoạn ngoài quy trình
                    </Button>
                    <span className="VcMuted">
                        — không thuộc quy trình của SKU này nên không sinh giờ công; xem khi cần đối chiếu
                        &ldquo;vì sao SKU này không có giờ ở công đoạn đó&rdquo;.
                    </span>
                </div>
            )}

            {result && result.status !== 'missing_routing' && (
                <Table
                    className="VcTable VcLookupTable"
                    columns={columns}
                    dataSource={buildRows()}
                    loading={loading}
                    pagination={false}
                    size="middle"
                    rowClassName={(row) => {
                        const classes = row.isFirst ? [] : ['VcRow--child']

                        /*
                         * ⚠️ Dòng phụ (Theo yêu cầu / Ngoài quy trình) KHÔNG bao giờ tô đỏ.
                         *
                         *    Đỏ ở bảng này nghĩa là "thiếu định mức, phải đi khai" — mà công đoạn
                         *    ngoài quy trình của SKU thì không khai cũng chẳng sao. Tô đỏ là đẩy
                         *    người dùng đi làm một việc vô ích, và làm con số "Thiếu x/y" trông như
                         *    đếm sai vì số dòng đỏ nhiều hơn.
                         */
                        if (row.extra) return [...classes, 'VcRow--extra'].join(' ')

                        // BR-M16-45: đường cắt = 0 cho 0 giây HỢP LỆ, không tô đỏ. Chỉ tô đỏ khi
                        // thực sự không tìm được nhóm nào.
                        if (row.line.result.status !== 'matched') classes.push('VcRow--unconfigured')

                        return classes.join(' ')
                    }}
                />
            )}

            {renderSummary()}
        </div>
    )
}

LookupPage.propTypes = {
    history: PropTypes.object.isRequired,
    location: PropTypes.object.isRequired,
    // Modal của cha đặt `closable={false}` (không có ✕ ở góc) nên đây là đường đóng DUY NHẤT.
    onClose: PropTypes.func.isRequired,
}

export default LookupPage
